'use strict';

const templates = require('../templates');

function degToRad(degrees) {
  return degrees * Math.PI / 180;
}

function radToDeg(radians) {
    return radians * 180 / Math.PI;
}

function rand(min, max) {
    const diff = max - min;
    return Math.random() * diff + min;
}

function floatsEqual (a, b, tolerance) {
    if (!tolerance) {
        tolerance = 0.001;
    }
    return Math.abs(a - b) < tolerance;
}

const noMoreThanQueue = {};
function noMoreThan (fn, timeout) {
    return () => {
        if (!noMoreThanQueue[fn]) {
            noMoreThanQueue[fn] = {
                toRun: false,
                timeout: setTimeout(() => {
                    if (noMoreThanQueue[fn].toRun) {
                        fn();
                    }
                    noMoreThanQueue[fn] = void 0;
                }, timeout)
            };
            fn();
            return;
        }
        if (!noMoreThanQueue[fn].toRun) {
            noMoreThanQueue[fn].toRun = true;
        }
    };
}

function sanitizeDegrees (angle) {
    while (angle < 0) angle += 360;
    return angle % 360;
}

function mk(tag, parent, innerHTML) {
    const node = document.createElement(tag);
    if (parent) {
        parent.appendChild(node);
    }
    if (innerHTML) {
        node.innerHTML = innerHTML;
    }
    return node;
}

function mkLabelledCheckbox (labelStr, checked, parentNode, callback) {
    const label = mk('label', parentNode);
    label.innerText = labelStr;
    const input = mk('input', label);
    input.type = 'checkbox';
    input.checked = checked;
    if (callback) {
        input.addEventListener('change', callback);
    }
    return label;
}

function fillWithShapeOptions(select, min, max, selected) {
    let values = [];
    if (Array.isArray(min)) {
        values = min;
        selected = max;
    } else {
        for (let i = min; i <= max; i++) {
            values.push(i);
        }
    }
    values.forEach((i) => {
        const opt = mk('option', select);
        opt.value = i;
        opt.innerText = `${i} - ${polygonName(i)}`;
        if (selected === i) {
            opt.selected = true;
        }
    });
}

const specialCaseNames = {
    0: '[nothing]',
    1: 'Point',
    2: 'LineSegment',
    3: 'Triangle',
    4: 'Square',
    9: 'Nonagon',
    11: 'Hendecagon',
    12: 'Dodecagon',
    20: 'Icosagon',
};
const greek = [
    '',
    'hena',
    'di',
    'tri',
    'tetra',
    'penta',
    'hexa',
    'hepta',
    'octa',
    'ennea'
];

function polygonName(sides) {
    if (specialCaseNames[sides]) {
        return specialCaseNames[sides];
    }
    if (sides > 29) {
        return `Polygon(${sides})`;
    }
    if (sides >= 20) {
        return `Icosi${greek[sides % 20]}gon`;
    }
    if (sides >= 10) {
        return `${greek[sides % 10]}decagon`.capitalize();
    }
    return `${greek[sides]}gon`.capitalize();
}

function setupCanvas(node) {
    const canvas = node.querySelector('canvas');
    let canvasSideLength = canvas.offsetWidth;
    canvas.setAttribute('width', canvasSideLength + '');
    canvas.setAttribute('height', canvasSideLength + '');
    return [canvasSideLength, canvas.getContext('2d')];
}

function addLabeledSlider (node, label, min, max, current) {
    const control = templates.getByName('labeled-slider').cloneNode(true);
    node.appendChild(control);
    const nameNode = control.querySelector('.name');
    nameNode.innerText = label;
    const valueNode = control.querySelector('.value');
    const input = control.querySelector('input');
    input.setAttribute('min', min);
    input.setAttribute('max', max);
    input.setAttribute('value', current);
    return [valueNode, input];
}

function addToolbar (node, ctx, shape) {
    const newButtons = templates.getByName('standard-toolbar').cloneNode(true);
    const canvas = node.querySelector('canvas');
    let canvasSideLength = canvas.offsetWidth;
    node.appendChild(newButtons);
    node.querySelector('.tick').addEventListener('click', () => {
        shape.stopFractal();
        shape.tick(ctx);
    });
    node.querySelector('.fifty-ticks').addEventListener('click', () => {
        shape.stopFractal();
        setTimeout(() => {
            for (let i = 0; i < 50; i++) {
                shape.tick(ctx);
            }
        }, 50);
    });
    node.querySelector('.reset').addEventListener('click', () => {
        shape.stopFractal();
        ctx.clearRect(0, 0, canvasSideLength, canvasSideLength);
        shape.draw(ctx);
        shape.numberPoints(ctx);
    });
}

function addRunButton (node, ctx, shape, limit) {
    const newButtons = templates.getByName('run').cloneNode(true);
    const canvas = node.querySelector('canvas');
    let canvasSideLength = canvas.offsetWidth;
    node.appendChild(newButtons);
    node.querySelector('.runner').addEventListener('click', () => {
        shape.stopFractal();
        shape.fractify(ctx, limit);
    });
    node.querySelector('.stop').addEventListener('click', () => {
        shape.stopFractal();
    });
    node.querySelector('.reset').addEventListener('click', () => {
        shape.stopFractal();
        ctx.clearRect(0, 0, canvasSideLength, canvasSideLength);
        shape.draw(ctx);
    });
}

function mkSelect (options, wrap) {
    const select = mk('select', wrap);
    options.forEach(option => {
        const opt = mk('option', select);
        if (option.value) {
            opt.value = option.value;
            opt.innerText = option.display || option.value;
            opt.selected = option.selected;
        } else {
            opt.value = option;
            opt.innerText = option;
        }
    });
    return select;
}

const throttle = function (fn, delay) {
    delay = delay || 500;
    let timeout = null;
    let again = false;
    return () => {
        if (timeout) {
            again = true;
            return;
        }
        again = false;
        fn();
        timeout = setTimeout(() => {
            timeout = null;
            if (again) {
                fn();
                again = false;
            }
        }, delay);
    };
};

const ordinal = function (number) {
    const numberStr = number + '';
    const num = numberStr[numberStr.length - 1];
    switch (num) {
        case '1':
            return 'st';
        case '2':
            return 'nd';
        case '3':
            return 'rd';
        default:
            return 'th';
    }
};

function waitPromise (timeout) {
    return new Promise((res) => {
        setTimeout(res, timeout);
    });
};

module.exports = {
    addToolbar,
    addRunButton,
    addLabeledSlider,
    degToRad,
    radToDeg,
    rand,
    floatsEqual,
    setupCanvas,
    noMoreThan,
    fillWithShapeOptions,
    sanitizeDegrees,
    throttle,
    ordinal,
    mk,
    waitPromise,
    mkSelect,
    mkLabelledCheckbox,
    polygonName,
    getById : function (id) {
        return document.getElementById(id);
    }
};

